import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./_shared";
import { Sparkles, Bug, Wrench } from "lucide-react";

const TYPE_META = {
  feature: {
    label: "New",
    Icon: Sparkles,
    bg: "bg-[#FF5F15] text-white",
    rowBg: "bg-orange-50 dark:bg-[#FF5F15]/10",
    border: "border-l-[#FF5F15]",
  },
  fix: {
    label: "Fix",
    Icon: Bug,
    bg: "bg-emerald-600 text-white",
    rowBg: "bg-emerald-50 dark:bg-emerald-500/10",
    border: "border-l-emerald-500",
  },
  improvement: {
    label: "Improved",
    Icon: Wrench,
    bg: "bg-zinc-900 text-white",
    rowBg: "bg-zinc-50 dark:bg-[#151C27]",
    border: "border-l-zinc-700 dark:border-l-zinc-500",
  },
};

const fmtDate = (iso) =>
  // Parse at local noon so the displayed calendar day never shifts a day
  // backwards for viewers whose timezone is behind UTC (keeps NZ dates intact).
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

/**
 * /app/changelog — what's new feed for both office and field workers.
 *
 * Visiting the page stamps `localStorage.fl-changelog-seen` with the latest
 * release date so the "What's new" pill in the header turns off.
 */
export default function Changelog() {
  // Same feed as the "What's new" popup (backend/data/changelog.json).
  const [feed, setFeed] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api.get("/changelog").then(({ data }) => {
      setFeed(data.releases || []);
      try { localStorage.setItem("fl-changelog-seen", data.latest_date || ""); } catch { /* ignore */ }
      // Opening the page clears the red "What's new" badge.
      api.post("/changelog/seen").then(() => window.dispatchEvent(new Event("fl-whats-new-seen"))).catch(() => {});
    }).catch(() => setFailed(true));
  }, []);

  return (
    <div data-testid="changelog-page" className="px-4 py-4 md:p-6 lg:p-10 md:max-w-4xl md:mx-auto pb-24 lg:pb-10">
      <PageHeader
        tag="/ What's new"
        title="Changelog"
        subtitle="Latest features, fixes and improvements we've shipped to Fieldlink. Tap a row to read the full note."
      />

      <div className="mt-6 space-y-8" data-testid="changelog-list">
        {failed && <p role="alert" className="text-sm text-red-600">Couldn't load the changelog. Please refresh.</p>}
        {!feed && !failed && <p className="text-sm text-zinc-500">Loading…</p>}
        {(feed || []).map((release, ri) => (
          <section key={`${release.date}-${ri}`} data-testid={`changelog-release-${release.date}`}>
            {/* Release header */}
            <div className="flex items-baseline gap-3 flex-wrap pb-2 border-b-2 border-zinc-900 dark:border-zinc-100">
              <div className="font-display font-black uppercase tracking-tight text-2xl text-zinc-900 dark:text-zinc-100">
                {fmtDate(release.date)}
              </div>
              {release.version && (
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
                  {release.version}
                </span>
              )}
              <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
                {release.items.length} update{release.items.length === 1 ? "" : "s"}
              </span>
            </div>

            {/* Items */}
            <div className="mt-4 space-y-3">
              {release.items.map((it, i) => {
                const meta = TYPE_META[it.type] || TYPE_META.improvement;
                const { Icon } = meta;
                return (
                  <div
                    key={i}
                    className={`border-l-4 ${meta.border} ${meta.rowBg} p-4`}
                    data-testid={`changelog-item-${release.date}-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-sm shrink-0 ${meta.bg}`}>
                        <Icon className="w-3 h-3" />
                        {meta.label}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <h3 className="font-bold text-sm text-zinc-900 dark:text-zinc-100 leading-snug break-words">{it.title}</h3>
                          {it.area && (
                            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
                              · {it.area}
                            </span>
                          )}
                        </div>
                        {it.description && (
                          <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300 leading-snug whitespace-pre-wrap">{it.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-12 text-center text-xs text-zinc-500 dark:text-zinc-400 font-mono uppercase tracking-widest">
        That's it for now · Made in NZ 🇳🇿 by Fieldlink
      </div>
    </div>
  );
}
